import { useState, useEffect } from "react";
import { Navbar, fetchWithTimeout, pageStyle, mainContentStyle, cardStyle } from "../App";
import LocationDropdown from "../components/LocationDropdown";

const API_URL = process.env.REACT_APP_API_URL;
const FEEDBACK_URL = process.env.REACT_APP_FEEDBACK_FORM_URL || "https://docs.google.com/forms/d/e/1FAIpQLSczBgyx2dK3dE9zOHriGtw6376tppdUnuL9b6fMIx1dGAhmYw/viewform";
const LOCAL_TRACKED_ORDERS_KEY = "tracked_order_tokens_v1";

const getLocalTrackedTokens = () => {
  try {
    const raw = localStorage.getItem(LOCAL_TRACKED_ORDERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((t) => typeof t === "string" && t.trim()).map((t) => t.trim().toUpperCase())
      : [];
  } catch (_) {
    return [];
  }
};

const addLocalTrackedToken = (token) => {
  if (!token) return;
  const normalized = token.trim().toUpperCase();
  if (!normalized) return;
  const existing = getLocalTrackedTokens();
  if (!existing.includes(normalized)) {
    localStorage.setItem(LOCAL_TRACKED_ORDERS_KEY, JSON.stringify([...existing, normalized]));
  }
};

const mergeOrdersById = (existing, incoming) => {
  const map = new Map();
  (existing || []).forEach((row) => map.set(row.id, row));
  (incoming || []).forEach((row) => map.set(row.id, row));
  return Array.from(map.values());
};

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_API_URL}${url}`;
};


function TrackOrder() {
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authUser, setAuthUser] = useState(null);
  const [claimedFeedbackTokens, setClaimedFeedbackTokens] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);

  const [trackInput, setTrackInput] = useState("");
  const [trackError, setTrackError] = useState("");
  const [trackSuccess, setTrackSuccess] = useState(false);

  const fetchMyOrders = async () => {
    try {
      const tokenParam = getLocalTrackedTokens().join(",");
      const url = tokenParam
        ? `${API_URL}/orders/mine?tokens=${encodeURIComponent(tokenParam)}`
        : `${API_URL}/orders/mine`;
      const res = await fetchWithTimeout(url, { credentials: "include" });
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      setMenu(data);
    } catch (err) {
      console.error("Error fetching menu:", err);
    }
  };

  const fetchFormStatus = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/form-status`);
      const data = await res.json();
      setIsFormOpen(data.is_open);
      setActiveSessionId(data.session_id || null);
    } catch (err) {
      console.error("Error fetching form status:", err);
      setIsFormOpen(false);
      setActiveSessionId(null);
    }
  };

  const fetchQr = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/settings/duitnow_qr`);
      const data = await res.json();
      if (data.value) setQrUrl(data.value);
    } catch (err) {
      console.error("Error fetching QR:", err);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocations(data);
    } catch (err) {
      console.error("Error fetching locations:", err);
    }
  };

  const fetchAuthSession = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/auth/me`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data?.authenticated || !data?.user) {
        setAuthUser(null);
        return;
      }
      setAuthUser(data.user);
    } catch (_) {
      setAuthUser(null);
    }
  };

  const fetchFeedbackStatus = async () => {
    if (!authUser) {
      setClaimedFeedbackTokens([]);
      return;
    }
    try {
      const res = await fetchWithTimeout(`${API_URL}/loyalty/feedback/status`, { credentials: "include" });
      const data = await res.json();
      if (res.ok && Array.isArray(data?.claimed_order_tokens)) {
        setClaimedFeedbackTokens(
          data.claimed_order_tokens
            .map((token) => String(token || "").trim().toUpperCase())
            .filter(Boolean)
        );
      } else {
        setClaimedFeedbackTokens([]);
      }
    } catch (_) {
      setClaimedFeedbackTokens([]);
    }
  };

  useEffect(() => {
    Promise.allSettled([
      fetchMyOrders(),
      fetchMenu(),
      fetchFormStatus(),
      fetchQr(),
      fetchLocations(),
      fetchAuthSession(),
    ]);
  }, []);

  useEffect(() => {
    fetchFeedbackStatus();
  }, [authUser]);

  const getPrice = (itemName) => {
    const found = menu.find((m) => m.name === itemName);
    return found ? parseFloat(found.price) : 0;
  };

  const getDeliveryFee = (locationName) => {
    const found = locations.find((l) => l.name === locationName);
    return found ? parseFloat(found.delivery_fee) : 0;
  };

  // Group flat DB rows by order_token into logical orders
  const groupedOrders = Object.values(
    orders.reduce((acc, row) => {
      const token = row.order_token || `solo-${row.id}`;
      if (!acc[token]) {
        acc[token] = {
          token,
          name: row.name,
          phone: row.phone,
          location: row.location,
          status: row.status,
          payment_status: row.payment_status,
          paid: row.paid,
          payment_proof_url: row.payment_proof_url,
          session_id: row.session_id,
          rows: [],
        };
      }
      acc[token].rows.push(row);
      return acc;
    }, {})
  ).map((g) => {
    const itemsTotal = g.rows.reduce(
      (sum, r) => sum + getPrice(r.item) * r.quantity, 0
    );
    const deliveryFee = getDeliveryFee(g.location);
    return {
      ...g,
      itemsTotal,
      deliveryFee,
      grandTotal: itemsTotal + deliveryFee,
      totalQty: g.rows.reduce((s, r) => s + r.quantity, 0),
    };
  });

  const trackOrder = async (e) => {
    e.preventDefault();
    setTrackError("");
    setTrackSuccess(false);

    if (!trackInput.trim()) return setTrackError("Please enter an Order ID.");

    try {
      const normalizedToken = trackInput.trim().toUpperCase();
      const res = await fetchWithTimeout(`${API_URL}/orders/track/${encodeURIComponent(normalizedToken)}`, {
        credentials: "include",
      });
      const data = await res.json();

      if (!res.ok) {
        setTrackError("Order not found. Double check your Order ID.");
        return;
      }

      addLocalTrackedToken(normalizedToken);
      if (Array.isArray(data.orders) && data.orders.length) {
        setOrders((prev) => mergeOrdersById(prev, data.orders));
      } else {
        fetchMyOrders();
      }
      setTrackSuccess(true);
      setTrackInput("");
    } catch (err) {
      setTrackError("Something went wrong. Try again.");
    }
  };

  return (
    <div className="mama-page" style={pageStyle}>
      <Navbar />
      <div className="mama-main" style={mainContentStyle}>
        <div className="mama-card" style={cardStyle}>
          <h1 className="mama-heading" style={{ marginTop: 0, marginBottom: "1.25rem" }}>Your Orders</h1>

          {!isFormOpen && (
            <div className="mama-closed-banner">
              ⏰ Orders are currently closed. Editing and cancelling is disabled.
            </div>
          )}

          {loading ? (
            <p style={{ color: "var(--text-muted)" }}>Loading your orders...</p>
          ) : groupedOrders.length > 0 ? (
            <>
              <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "1rem" }}>
                Orders from this device:
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {groupedOrders.map((group) => (
                  <OrderCard
                    key={group.token}
                    group={group}
                    authUser={authUser}
                    claimedFeedbackTokens={claimedFeedbackTokens}
                    onFeedbackClaimed={fetchFeedbackStatus}
                    isFormOpen={isFormOpen}
                    activeSessionId={activeSessionId}
                    onRefresh={fetchMyOrders}
                    menu={menu}
                    qrUrl={qrUrl}
                    locations={locations}
                  />
                ))}
              </ul>
            </>
          ) : (
            <div className="mama-empty-state">
              <div className="mama-empty-emoji">🍽️</div>
              <p style={{ margin: 0, fontWeight: 600 }}>No orders found on this device.</p>
              <p style={{ margin: "0.5rem 0 0", fontSize: "0.85rem" }}>
                Placed an order on a different device? Use the lookup below.
              </p>
            </div>
          )}

          <hr className="mama-divider" />
          <h2 style={{ marginTop: 0, fontSize: "1.1rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" }}>
            🔍 Look up by Order ID
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", marginTop: 0 }}>
            Switched devices or cleared your browser? Enter your Order ID to find it.
          </p>

          <form onSubmit={trackOrder} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
            <input
              value={trackInput}
              onChange={(e) => setTrackInput(e.target.value.toUpperCase())}
              className="mama-input"
              style={{ marginBottom: 0, flex: 1 }}
            />
            <button type="submit" className="mama-btn-primary" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>Find</button>
          </form>

          {trackError && (
            <p className="mama-error" style={{ marginTop: "0.5rem" }}>{trackError}</p>
          )}

          {trackSuccess && (
            <p style={{ color: "var(--success)", fontSize: "0.9rem", marginTop: "0.5rem", fontWeight: 600 }}>
              ✅ Order found and added to this device — scroll up to see it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}


function OrderCard({ group, authUser, claimedFeedbackTokens, onFeedbackClaimed, isFormOpen, activeSessionId, onRefresh, menu, qrUrl, locations }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);
  const [editPhone, setEditPhone] = useState(group.phone || "");
  const [editLocationDropdown, setEditLocationDropdown] = useState(group.location || "");
  const [editCustomLocation, setEditCustomLocation] = useState("");
  const [error, setError] = useState("");
  const [feedbackClaimLoading, setFeedbackClaimLoading] = useState(false);
  const [feedbackClaimError, setFeedbackClaimError] = useState("");
  const [feedbackClaimSuccess, setFeedbackClaimSuccess] = useState("");

  const [proofFile, setProofFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");

  const getStudentStatus = (status, paymentStatus) => {
    if (paymentStatus === "submitted" || paymentStatus === "unpaid" || paymentStatus === "rejected") {
      return { label: "⏳ Confirming payment...", badgeClass: "mama-badge mama-badge-pending" };
    }
    if (paymentStatus === "confirmed" && status === "Pending") {
      return { label: "👩‍🍳 Cooking your order!", badgeClass: "mama-badge mama-badge-cooking" };
    }
    if (status === "Ready") {
      return { label: "🛵 On the way!", badgeClass: "mama-badge mama-badge-ready" };
    }
    if (status === "Completed") {
      return { label: "✅ Delivered!", badgeClass: "mama-badge mama-badge-completed" };
    }
    return { label: "⏳ Confirming payment...", badgeClass: "mama-badge mama-badge-pending" };
  };

  const sConf = getStudentStatus(group.status, group.payment_status);

  const accentClass = group.status === 'Ready'
    ? 'mama-order-card--ready'
    : group.status === 'Completed'
      ? 'mama-order-card--completed'
      : 'mama-order-card--pending';

  const isCurrentSession = activeSessionId !== null && group.session_id === activeSessionId;
  const canModify = isFormOpen && isCurrentSession && group.status === "Pending";

  const openEdit = () => {
    setEditName(group.name);
    setEditPhone(group.phone || "");
    const isCustom = group.location && !locations.find(l => l.name === group.location);
    if (isCustom) {
      setEditLocationDropdown("OTHER");
      setEditCustomLocation(group.location);
    } else {
      setEditLocationDropdown(group.location || "");
      setEditCustomLocation("");
    }
    setError("");
    setEditing(true);
  };

  const handleEdit = async () => {
    setError("");
    const phoneRegex = /^\+?[0-9]{7,15}$/;
    if (!editName.trim()) return setError("Name cannot be empty.");
    if (!phoneRegex.test(editPhone)) return setError("WhatsApp number must be 7–15 digits (include country code, e.g. +60123456789).");
    if (!editLocationDropdown) return setError("Please select a location.");
    if (editLocationDropdown === "OTHER" && !editCustomLocation.trim()) return setError("Please enter your custom address or Google Maps link.");

    try {
      const finalLocation = editLocationDropdown === "OTHER" ? editCustomLocation : editLocationDropdown;
      for (const row of group.rows) {
        const res = await fetch(`${API_URL}/orders/${row.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: editName,
            phone: editPhone,
            item: row.item,
            quantity: row.quantity,
            location: finalLocation,
            order_token: group.token,
          }),
        });
        const data = await res.json();
        if (!res.ok) return setError(data.error || "Failed to update order.");
      }
      setEditing(false);
      onRefresh();
    } catch (err) {
      setError("Something went wrong.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Cancel this order (${group.totalQty} item${group.totalQty !== 1 ? "s" : ""})? This cannot be undone.`)) return;
    try {
      for (const row of group.rows) {
        const res = await fetch(`${API_URL}/orders/${row.id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ order_token: group.token }),
        });
        if (!res.ok) return alert("Failed to cancel order.");
      }
      onRefresh();
    } catch (err) {
      alert("Something went wrong.");
    }
  };

  const handleUploadProof = async (e) => {
    e.preventDefault();
    setUploadError(""); setUploadSuccess("");
    if (!proofFile) return setUploadError("Please select a file.");
    setUploading(true);

    const formData = new FormData();
    formData.append("proof", proofFile);
    formData.append("order_token", group.token);

    try {
      const res = await fetch(`${API_URL}/orders/${group.rows[0].id}/payment-proof`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { setUploadError(data.error || "Upload failed."); setUploading(false); return; }
      setUploadSuccess("Payment proof submitted! The admin will confirm shortly.");
      setProofFile(null);
      onRefresh();
    } catch (err) {
      setUploadError("Upload failed. Try again.");
    }
    setUploading(false);
  };

  const uploadId = `proof-upload-${group.token}`;
  const normalizedToken = String(group.token || "").trim().toUpperCase();
  const feedbackAlreadyClaimed = claimedFeedbackTokens.includes(normalizedToken);

  const claimFeedbackPoints = async () => {
    if (!authUser || !normalizedToken) return;
    setFeedbackClaimError("");
    setFeedbackClaimSuccess("");
    setFeedbackClaimLoading(true);
    try {
      const res = await fetchWithTimeout(
        `${API_URL}/loyalty/feedback/claim`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ order_token: normalizedToken }),
        },
        12000
      );
      const data = await res.json();
      if (!res.ok) {
        setFeedbackClaimError(data.error || "Could not claim feedback points.");
        setFeedbackClaimLoading(false);
        return;
      }
      setFeedbackClaimSuccess("Feedback points claimed: +3");
      onFeedbackClaimed?.();
    } catch (_) {
      setFeedbackClaimError("Could not claim feedback points.");
    } finally {
      setFeedbackClaimLoading(false);
    }
  };

  return (
    <li className={`mama-order-card ${accentClass}`}>

      {/* ===== View Mode ===== */}
      {!editing && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
            <div>
              <strong style={{ fontSize: "1rem", color: "var(--text-main)" }}>{group.name}</strong>
              {group.phone && (
                <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  📞 {group.phone}
                </p>
              )}
              {group.rows.map((row) => (
                <p key={row.id} style={{ margin: "0.25rem 0 0", color: "var(--text-main)" }}>
                  {row.quantity}× {row.item}
                </p>
              ))}
              {group.location && (
                <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  📍 {group.location}
                </p>
              )}
              <p style={{ margin: "0.25rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)", opacity: 0.7 }}>
                {group.token}
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.4rem", flexShrink: 0, maxWidth: "160px" }}>
              <span className={sConf.badgeClass} style={{ textAlign: "center", whiteSpace: "normal", wordBreak: "break-word" }}>{sConf.label}</span>
            </div>
          </div>

          {canModify && (
            <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
              <button onClick={openEdit} className="mama-btn-edit">✏️ Edit Order</button>
              <button onClick={handleDelete} className="mama-btn-delete">🗑 Cancel Order</button>
            </div>
          )}

          {!canModify && group.status === "Pending" && (
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.8rem", color: "var(--text-muted)" }}>
              {!isCurrentSession
                ? "This order is from a previous session."
                : "Order window is closed — editing disabled."}
            </p>
          )}

          {group.status === "Completed" && (
            <div className="mama-feedback">
              <p className="mama-feedback-text">
                ⭐ How was your order? Your feedback helps us improve!
              </p>
              <a
                href={FEEDBACK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mama-feedback-btn"
              >
                Leave Feedback →
              </a>
              {authUser ? (
                <div style={{ marginTop: "0.6rem" }}>
                  <button
                    type="button"
                    className="mama-btn-primary"
                    onClick={claimFeedbackPoints}
                    disabled={feedbackClaimLoading || feedbackAlreadyClaimed}
                    style={{ fontSize: "0.78rem", padding: "0.45rem 0.75rem" }}
                  >
                    {feedbackClaimLoading
                      ? "Claiming..."
                      : feedbackAlreadyClaimed
                      ? "Already claimed"
                      : "Claim +3 points"}
                  </button>
                  {feedbackClaimSuccess && (
                    <p style={{ margin: "0.4rem 0 0", fontSize: "0.78rem", color: "var(--success)", fontWeight: 700 }}>
                      {feedbackClaimSuccess}
                    </p>
                  )}
                  {feedbackClaimError && (
                    <p className="mama-error" style={{ margin: "0.4rem 0 0" }}>{feedbackClaimError}</p>
                  )}
                </div>
              ) : (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  Log in via Account tab to claim +3 points.
                </p>
              )}
            </div>
          )}

          {/* ===== Payment Section ===== */}
          {(!group.payment_status || group.payment_status === "unpaid" || group.payment_status === "rejected") && (
            <div style={{ marginTop: "1rem", borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
              {group.payment_status === "rejected" && (
                <p style={{ margin: "0 0 0.75rem", color: "var(--error)", fontSize: "0.88rem", fontWeight: "bold" }}>
                  ❌ Your payment was rejected. Please re-upload the correct proof.
                </p>
              )}

              <p style={{ margin: "0 0 0.5rem", fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>
                💳 Pay via DuitNow
              </p>

              <div className="mama-amount-box">
                {group.rows.map((row) => {
                  const menuItem = menu.find((m) => m.name === row.item);
                  const lineTotal = menuItem ? parseFloat(menuItem.price) * row.quantity : 0;
                  return (
                    <div className="mama-amount-row" key={row.id}>
                      <span>{row.quantity}× {row.item}</span>
                      <span>RM {lineTotal.toFixed(2)}</span>
                    </div>
                  );
                })}
                <div className="mama-amount-row">
                  <span>Delivery to {group.location}</span>
                  <span>{group.deliveryFee === 0 ? "Free" : `RM ${group.deliveryFee.toFixed(2)}`}</span>
                </div>
                <div className="mama-amount-total">
                  <span>Total to pay</span>
                  <span>RM {group.grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {qrUrl ? (
                <div style={{ marginBottom: "1rem", textAlign: "center" }}>
                  <img
                    src={resolveUrl(qrUrl)}
                    alt="DuitNow QR"
                    style={{ maxWidth: "180px", border: "1px solid var(--border)", borderRadius: "12px", padding: "0.4rem", background: "#fff" }}
                  />
                  <p style={{ margin: "0.4rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                    Scan to pay, then upload your receipt below
                  </p>
                </div>
              ) : (
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                  Payment QR not set up yet — contact admin.
                </p>
              )}

              <form onSubmit={handleUploadProof}>
                <label className="mama-label" style={{ textTransform: "none", fontSize: "0.88rem" }}>
                  Upload payment proof <span style={{ color: "var(--error)" }}>*</span>
                  <span style={{ fontWeight: "normal", color: "var(--text-muted)", marginLeft: "0.3rem" }}>(screenshot or PDF)</span>
                </label>
                <div
                  className="mama-upload-zone"
                  onClick={() => document.getElementById(uploadId).click()}
                >
                  <input
                    id={uploadId}
                    type="file"
                    accept="image/*,application/pdf"
                    required
                    onChange={e => setProofFile(e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  {proofFile ? (
                    <p className="mama-upload-ready">✅ {proofFile.name} selected</p>
                  ) : (
                    <>
                      <span className="mama-upload-icon">📎</span>
                      <span className="mama-upload-text">Tap to upload receipt</span>
                    </>
                  )}
                </div>
                {uploadError && <p className="mama-error" style={{ margin: "0 0 0.5rem" }}>{uploadError}</p>}
                <button
                  type="submit"
                  disabled={uploading || !proofFile}
                  className="mama-btn-primary"
                  style={{
                    opacity: (uploading || !proofFile) ? 0.4 : 1,
                    cursor: (uploading || !proofFile) ? "not-allowed" : "pointer",
                    boxShadow: proofFile ? "0 4px 14px rgba(224,123,57,0.35)" : "none",
                    fontSize: "0.9rem",
                    padding: "0.55rem 1.25rem",
                  }}
                >
                  {uploading ? "Uploading..." : "Submit Payment Proof"}
                </button>
                {!proofFile && (
                  <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.4rem" }}>
                    You must attach proof before submitting.
                  </p>
                )}
              </form>
            </div>
          )}

          {group.payment_status === "submitted" && (
            <div style={{ marginTop: "1rem", borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
              <p style={{ margin: 0, fontSize: "0.88rem", color: "var(--brown-mid)", fontWeight: 600 }}>
                ✅ Payment proof submitted — waiting for admin confirmation.
              </p>
            </div>
          )}

          {group.payment_status === "confirmed" && (
            <div style={{ marginTop: "1rem", borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
              <p style={{ margin: 0, fontSize: "0.88rem", color: "var(--success)", fontWeight: "bold" }}>
                ✅ Payment confirmed. Thank you!
              </p>
            </div>
          )}

          {uploadSuccess && (
            <p style={{ marginTop: "0.75rem", color: "var(--success)", fontSize: "0.88rem", fontWeight: 600 }}>{uploadSuccess}</p>
          )}
        </>
      )}

      {/* ===== Edit Mode ===== */}
      {editing && (
        <div>
          <p style={{ margin: "0 0 0.75rem", fontWeight: "bold", color: "var(--text-main)", fontFamily: "'Playfair Display', Georgia, serif" }}>
            ✏️ Edit your order
          </p>

          {group.paid && (
            <div style={{
              background: "#FEF4E4",
              border: "1px solid var(--orange-light)",
              borderRadius: "10px",
              padding: "0.6rem 0.9rem",
              fontSize: "0.85rem",
              color: "var(--brown-mid)",
              marginBottom: "1rem",
            }}>
              🔒 Payment confirmed — items and location are locked. You can still update your name and phone.
            </div>
          )}

          <label className="mama-label">Name</label>
          <input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="mama-input"
            style={{ marginBottom: "0.85rem" }}
          />

          <label className="mama-label">WhatsApp Number</label>
          <input
            value={editPhone}
            onChange={(e) => setEditPhone(e.target.value)}
            className="mama-input"
            style={{ marginBottom: "0.85rem" }}
            placeholder="+60123456789"
          />
          <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "-0.5rem 0 0.75rem" }}>
            📱 Include your country code (e.g. +60123456789 for Malaysia, +971501234567 for UAE)
          </p>

          <label className="mama-label">
            Items {group.paid && <span style={{ color: "var(--text-muted)", fontWeight: "normal", textTransform: "none" }}>🔒 locked</span>}
          </label>
          <div style={{ marginBottom: "0.85rem" }}>
            {group.rows.map((row) => (
              <div key={row.id} className="mama-locked-field" style={{ marginBottom: "0.4rem" }}>
                {row.quantity}× {row.item}
              </div>
            ))}
          </div>

          <label className="mama-label">
            Location {group.paid && <span style={{ color: "var(--text-muted)", fontWeight: "normal", textTransform: "none" }}>🔒 locked</span>}
          </label>
          {group.paid ? (
            <div className="mama-locked-field">
              {group.location}
            </div>
          ) : (
            <>
              <LocationDropdown
                id={`track-edit-location-${group.token}`}
                value={editLocationDropdown}
                onChange={setEditLocationDropdown}
                locations={locations}
              />
              {editLocationDropdown === "OTHER" && (
                <>
                  <input
                    value={editCustomLocation}
                    onChange={(e) => setEditCustomLocation(e.target.value)}
                    className="mama-input"
                    placeholder="Enter your address or paste Google Maps link"
                    style={{ marginTop: "0.75rem", marginBottom: "0.5rem" }}
                  />
                  <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0 0 0.75rem" }}>
                    💡 Tip: You can paste your Google Maps link here for exact location
                  </p>
                </>
              )}
            </>
          )}

          {error && <p className="mama-error" style={{ margin: "0.25rem 0 0.5rem" }}>{error}</p>}

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
            <button onClick={handleEdit} className="mama-btn-save">Save Changes</button>
            <button onClick={() => setEditing(false)} className="mama-btn-cancel">Discard</button>
          </div>
        </div>
      )}
    </li>
  );
}

export default TrackOrder;